import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Users as UsersIcon,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Shield,
  Mail,
  Bell,
  Send,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Key
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const Users = () => {
  const { getAuthHeaders, user: currentUser, isAdmin, isDemoReadonly } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'superviseur',
    site_ids: [],
    reports_to: null
  });

  // Escalation state
  const [checkingEscalations, setCheckingEscalations] = useState(false);
  const [escalationResult, setEscalationResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, sitesRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() })
      ]);
      setUsers(usersRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        const updates = { ...form };
        if (!updates.password) delete updates.password;
        await axios.put(`${API}/users/${editingUser.id}`, updates, { headers: getAuthHeaders() });
        toast.success('Utilisateur mis à jour');
      } else {
        if (!form.password) {
          toast.error('Mot de passe requis');
          return;
        }
        await axios.post(`${API}/users`, form, { headers: getAuthHeaders() });
        toast.success('Utilisateur créé');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    if (!window.confirm(`Supprimer l'utilisateur "${user.name}" ?`)) return;
    
    const headers = getAuthHeaders();
    if (!headers.Authorization || headers.Authorization === 'Bearer ') {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${user.id}`, { headers });
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      }
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      site_ids: user.site_ids || [],
      reports_to: user.reports_to || null
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingUser(null);
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      password: '',
      role: 'superviseur',
      site_ids: [],
      reports_to: null
    });
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-blue-100 text-blue-800',
      superviseur: 'bg-green-100 text-green-800',
      lecteur: 'bg-slate-100 text-slate-800'
    };
    return <Badge className={roleColors[role] || 'bg-slate-100'}>{t(`roles.${role}`) || role}</Badge>;
  };

  const getSiteNames = (siteIds) => {
    if (!siteIds || siteIds.length === 0) return t('users.noSites');
    return siteIds.map(id => sites.find(s => s.id === id)?.name).filter(Boolean).join(', ');
  };

  const toggleSite = (siteId) => {
    const newSiteIds = form.site_ids.includes(siteId)
      ? form.site_ids.filter(id => id !== siteId)
      : [...form.site_ids, siteId];
    setForm({ ...form, site_ids: newSiteIds });
  };

  // Password change functions
  const openPasswordChange = (user) => {
    setPasswordTarget(user);
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    const isSelf = passwordTarget?.id === currentUser?.id;
    
    // Self-change requires current password
    if (isSelf && !passwordForm.current_password) {
      toast.error('Veuillez entrer votre mot de passe actuel');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/users/${passwordTarget.id}/change-password`,
        {
          current_password: isSelf ? passwordForm.current_password : null,
          new_password: passwordForm.new_password
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Mot de passe modifié avec succès');
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  // Escalation functions
  const checkEscalations = async () => {
    setCheckingEscalations(true);
    setEscalationResult(null);
    try {
      const response = await axios.post(`${API}/escalations/check`, {}, { headers: getAuthHeaders() });
      setEscalationResult(response.data);
      if (response.data.results?.escalated > 0) {
        toast.success(`${response.data.results.escalated} notification(s) d'escalade envoyée(s)`);
      } else {
        toast.info('Aucune nouvelle escalade à notifier');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
    } finally {
      setCheckingEscalations(false);
    }
  };

  const sendTestEscalationEmail = async () => {
    if (!testEmail) {
      toast.error('Entrez une adresse email');
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await axios.post(
        `${API}/escalations/test-email?recipient_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data.status === 'success') {
        toast.success('Email de test envoyé !');
      } else if (response.data.status === 'skipped') {
        toast.warning(`Email non envoyé: ${response.data.reason}`);
      } else {
        toast.error(response.data.message || 'Erreur');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="p-4 md:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-16 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium">Accès réservé aux administrateurs</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">{t('users.title')}</h1>
          <p className="text-slate-500 mt-1">{users.length} {t('users.title').toLowerCase()}</p>
        </div>
        {!isDemoReadonly() && (
          <Button onClick={openNew} className="bg-[#0E346E] hover:bg-[#0A2850]" data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('users.newUser')}
          </Button>
        )}
      </div>

      {/* Users Grid - Professional SaaS Layout */}
      {users.length === 0 ? (
        <Card className="border-2 border-dashed border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <UsersIcon className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun utilisateur</h3>
            <p className="text-slate-500 text-sm mb-4">Commencez par ajouter votre premier utilisateur</p>
            <Button onClick={openNew} className="bg-[#0E346E] hover:bg-[#0A2850]">
              <Plus className="w-4 h-4 mr-2" />
              {t('users.newUser')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {users.map((user) => (
            <Card 
              key={user.id} 
              className="group relative bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-200 overflow-hidden"
              data-testid={`user-card-${user.id}`}
            >
              {/* Action Buttons - Top Right Corner */}
              <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8 bg-white/80 hover:bg-amber-50 backdrop-blur-sm shadow-sm"
                  onClick={() => openPasswordChange(user)}
                  title="Réinitialiser le mot de passe"
                  data-testid={`change-password-btn-${user.id}`}
                >
                  <Key className="w-4 h-4 text-amber-600" />
                </Button>
                {user.id !== currentUser?.id && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 bg-white/80 hover:bg-blue-50 backdrop-blur-sm shadow-sm"
                      onClick={() => openEdit(user)}
                      title="Modifier l'utilisateur"
                    >
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 bg-white/80 hover:bg-red-50 backdrop-blur-sm shadow-sm"
                      onClick={() => handleDelete(user)}
                      title="Supprimer l'utilisateur"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </>
                )}
              </div>

              <CardContent className="p-5">
                {/* User Info Section */}
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center shadow-inner">
                    <span className="text-xl font-bold text-slate-600">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Name & Email */}
                  <div className="flex-1 min-w-0 pr-20">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">{user.name}</h3>
                      {user.id === currentUser?.id && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0">
                          Vous
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate flex items-center gap-1.5 mt-1">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                      <span className="truncate">{user.email}</span>
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-slate-100 my-4" />

                {/* Role & Sites Section */}
                <div className="space-y-3">
                  {/* Role Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Rôle</span>
                    {getRoleBadge(user.role)}
                  </div>

                  {/* Assigned Sites */}
                  <div>
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">
                      Sites assignés
                    </span>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {getSiteNames(user.site_ids) || (
                        <span className="text-slate-400 italic">Aucun site</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Mobile Actions - Always Visible on Small Screens */}
                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-slate-100 md:hidden">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-8"
                    onClick={() => openPasswordChange(user)}
                  >
                    <Key className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
                    Mot de passe
                  </Button>
                  {user.id !== currentUser?.id && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-8"
                        onClick={() => openEdit(user)}
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                        Modifier
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDelete(user)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Escalation Notifications Section */}
      <Card className="border-0 shadow-md bg-gradient-to-br from-[#0E346E] to-[#1E4A8E]">
        <CardHeader>
          <CardTitle className="text-xl font-['Barlow_Condensed'] text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-[#B98D32] rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            {t('escalations.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Explanation */}
          <div className="bg-white/10 rounded-lg p-4 text-white/90 text-sm">
            <p className="font-medium mb-2">{t('escalations.rules')}</p>
            <ul className="space-y-1 text-white/80">
              <li>• <span className="text-amber-300 font-semibold">{t('actions.escalationLevels.j1')}</span> : {t('escalations.j1Rule')}</li>
              <li>• <span className="text-orange-300 font-semibold">{t('actions.escalationLevels.j3')}</span> : {t('escalations.j3Rule')}</li>
              <li>• <span className="text-red-300 font-semibold">{t('actions.escalationLevels.j7')}</span> : {t('escalations.j7Rule')}</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Check Escalations */}
            <Card className="bg-white">
              <CardContent className="p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-[#0E346E]" />
                  {t('escalations.checkEscalations')}
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  {t('escalations.checkDescription')}
                </p>
                <Button
                  onClick={checkEscalations}
                  disabled={checkingEscalations}
                  className="w-full bg-[#0E346E] hover:bg-[#0A2850]"
                  data-testid="check-escalations-btn"
                >
                  {checkingEscalations ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('escalations.checking')}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('escalations.launchCheck')}
                    </>
                  )}
                </Button>

                {/* Results */}
                {escalationResult && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      {escalationResult.results?.escalated > 0 ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      )}
                      <span className="font-medium">
                        {escalationResult.results?.processed} {t('escalations.actionsAnalyzed')}
                      </span>
                    </div>
                    <p className="text-gray-600">
                      {escalationResult.results?.escalated} {t('escalations.notificationsSent')}
                    </p>
                    {escalationResult.results?.errors?.length > 0 && (
                      <p className="text-red-600 mt-1">
                        {escalationResult.results.errors.length} {t('escalations.errors')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Test Email */}
            <Card className="bg-white">
              <CardContent className="p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-[#B98D32]" />
                  {t('escalations.testEmail')}
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  {t('escalations.testDescription')}
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button
                    onClick={sendTestEscalationEmail}
                    disabled={sendingTestEmail || !testEmail}
                    variant="outline"
                    className="border-[#B98D32] text-[#B98D32] hover:bg-[#B98D32] hover:text-white"
                    data-testid="send-test-email-btn"
                  >
                    {sendingTestEmail ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Configuration Note */}
          <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-4 text-white text-sm">
            <p className="font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('escalations.configRequired')}
            </p>
            <p className="mt-1 text-white/80">
              {t('escalations.configDescription')}
              <br />
              <code className="bg-black/20 px-2 py-1 rounded mt-2 inline-block">RESEND_API_KEY=re_votre_cle_api</code>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl text-gray-900">
              {editingUser ? t('users.editUser') : t('users.newUser')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{t('users.form.name')} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jean Dupont"
                className="h-12 bg-white border-2 border-gray-300 focus:border-[#0E346E] text-gray-900 placeholder:text-gray-400"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{t('users.form.email')} *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jean@exemple.com"
                className="h-12 bg-white border-2 border-gray-300 focus:border-[#0E346E] text-gray-900 placeholder:text-gray-400"
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{editingUser ? t('users.form.newPassword') : t('users.form.password')} *</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="h-12 bg-white border-2 border-gray-300 focus:border-[#0E346E] text-gray-900 placeholder:text-gray-400"
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{t('users.form.role')}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-12 bg-white border-2 border-gray-300 text-gray-900" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="admin">{t('roles.admin')}</SelectItem>
                  <SelectItem value="manager">{t('roles.manager')}</SelectItem>
                  <SelectItem value="superviseur">{t('roles.supervisor')}</SelectItem>
                  <SelectItem value="lecteur">{t('roles.reader')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reports To - Hierarchy */}
            {form.role === 'superviseur' && (
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Supérieur hiérarchique</Label>
                <Select value={form.reports_to || 'none'} onValueChange={(v) => setForm({ ...form, reports_to: v === 'none' ? null : v })}>
                  <SelectTrigger className="h-12 bg-white border-2 border-gray-300 text-gray-900">
                    <SelectValue placeholder="Sélectionner un responsable" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Aucun</SelectItem>
                    {users.filter(u => u.role === 'admin' || u.role === 'manager').map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role === 'admin' ? 'Admin' : 'Manager'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Les tournées clôturées seront envoyées à ce responsable</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{t('users.form.assignedSites')}</Label>
              <div className="flex flex-wrap gap-2 p-4 border-2 border-gray-300 rounded-lg bg-gray-50 min-h-[70px]">
                {sites.map((site) => (
                  <Badge
                    key={site.id}
                    variant={form.site_ids.includes(site.id) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all text-sm py-1.5 px-3 ${form.site_ids.includes(site.id) ? 'bg-[#0E346E] text-white' : 'bg-white text-gray-700 border-gray-400 hover:bg-gray-100'}`}
                    onClick={() => toggleSite(site.id)}
                  >
                    {site.name}
                  </Badge>
                ))}
                {sites.length === 0 && (
                  <p className="text-sm text-gray-500">{t('users.form.noSites')}</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={handleSave}
              disabled={!form.name || !form.email || (!editingUser && !form.password)}
              className="bg-[#0E346E] hover:bg-[#0A2850]"
              data-testid="save-user-btn"
            >
              {editingUser ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-white sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              {t('users.changePassword')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {passwordTarget && (
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">{t('users.form.user')} :</p>
                <p className="font-semibold text-slate-900">{passwordTarget.name}</p>
                <p className="text-sm text-slate-500">{passwordTarget.email}</p>
              </div>
            )}

            {passwordTarget?.id === currentUser?.id && (
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">{t('users.form.currentPassword')} *</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder={t('users.form.enterCurrentPassword')}
                  className="h-12 bg-white border-2 border-gray-300 focus:border-[#0E346E] text-gray-900"
                  data-testid="current-password-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{t('users.form.newPasswordLabel')} *</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder={t('users.form.minChars')}
                className="h-12 bg-white border-2 border-gray-300 focus:border-[#0E346E] text-gray-900"
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">{t('users.form.confirmPassword')} *</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder={t('users.form.repeatPassword')}
                className="h-12 bg-white border-2 border-gray-300 focus:border-[#0E346E] text-gray-900"
                data-testid="confirm-password-input"
              />
            </div>

            {passwordTarget?.id !== currentUser?.id && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p className="font-medium">{t('common.note')} :</p>
                <p>{t('users.form.adminResetNote')}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordForm.new_password || !passwordForm.confirm_password}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-password-btn"
            >
              {changingPassword ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
